import * as DataDog from '@datadog/browser-rum-core';
import { QuantumAppEnvNameType } from '../../contexts/types/quantum';
import { IEnvConfig } from '../envConfig/envConfig.types';
import { AppEnv, PiTargetBg, IAppConfigApiHosts } from './appConfig.types';

/**
 * Converts app env to its corresponding enum value
 */
export const toAppEnvEnum = (enumValue: string): AppEnv => {
  const keys = Object.keys(AppEnv).filter(
    (x) =>
      AppEnv[x as keyof typeof AppEnv].toLowerCase() === enumValue.toLowerCase()
  );
  const key = keys.length > 0 ? keys[0] : AppEnv.Mock;
  return AppEnv[key as keyof typeof AppEnv];
};

/**
 * Gets the version name (ie: branch name)
 */
export const getAppVersionName = (envConfig: IEnvConfig): string => {
  return envConfig.ci_commit_ref_slug;
};

/**
 * Get the app full version by combining the Gitlab branch (or tag) name and the
 * pipeline ID it was deployed with.
 */
export const getAppPackageVersion = (envConfig: IEnvConfig): string => {
  const name = getAppVersionName(envConfig);
  const buildId = Number(envConfig.ci_pipeline_id);

  return name + (buildId ? `_${buildId}` : '');
};

/**
 * Attempts to determine the environment by URL.
 */
export const getAppEnvByUrl = (): AppEnv => {
  const host = window.location.hostname.toLowerCase();

  if (
    host === 'cxdo.spectrumenterprise.net' ||
    host === 'cxdo-blue.spectrumenterprise.net' ||
    host === 'cxdo-green.spectrumenterprise.net'
  ) {
    return AppEnv.Prod;
  }

  if (host === 'cxdo.stage.spectrumenterprise.net') {
    return AppEnv.Stage;
  }

  if (host.includes('engprod')) {
    return AppEnv.EngProd;
  }

  if (host.includes('uat')) {
    return AppEnv.Uat;
  }

  if (host.includes('qa')) {
    return AppEnv.QA;
  }

  if (host.includes('dev')) {
    return AppEnv.Dev;
  }

  return AppEnv.Mock;
};

/**
 * Get the current app environment based on the URL
 */
export const getAppEnv = (envConfig?: IEnvConfig): AppEnv => {
  // Used for Local Development
  if (import.meta.env.VITE_APP_APP_ENV) {
    return toAppEnvEnum(import.meta.env.VITE_APP_APP_ENV as string);
  }

  // All Apps being deployed via PDE pipeline will use this var to determine env.
  if (envConfig && envConfig['app-env']) {
    return toAppEnvEnum(envConfig['app-env']);
  }

  // This is only used as a fall-back if for some reason the above logic falls thru.
  return getAppEnvByUrl();
};

/**
 * Tell Pi which BE endpoint to route to.
 */
export const getTargetBg = (): PiTargetBg => {
  const hostName = window.location.hostname.toLowerCase();
  const appEnv = getAppEnv();
  if (hostName.startsWith('cxdo-blue.')) {
    return PiTargetBg.Blue;
  }

  if (hostName.startsWith('cxdo-green.')) {
    return PiTargetBg.Green;
  }
  switch (appEnv) {
    case AppEnv.Uat:
      return PiTargetBg.Uat;
    case AppEnv.EngProd:
      return PiTargetBg.EngProd;
    case AppEnv.QA:
      return PiTargetBg.QA;
    case AppEnv.Stage:
      return PiTargetBg.Stage;
    default:
      return PiTargetBg.Default;
  }
};

/**
 * Get the correct App Url to use for SENet based on whether we are on Blue / Green.
 */
const getProdSenetHost = (): string => {
  const targetBg = getTargetBg();
  switch (targetBg) {
    case PiTargetBg.Blue:
      return 'https://blue.spectrumenterprise.net';
    case PiTargetBg.Green:
      return 'https://green.spectrumenterprise.net';
    default:
      return 'https://spectrumenterprise.net';
  }
};

/**
 * Get the correct App Url to use for SSO based on whether we are on Blue / Green.
 */
const getSSOProdHost = (): string => {
  const targetBg = getTargetBg();
  switch (targetBg) {
    case PiTargetBg.Blue:
      return 'https://blue-enterprise.apis.spectrumenterprise.net';
    case PiTargetBg.Green:
      return 'https://green-enterprise.apis.spectrumenterprise.net';
    default:
      return 'https://enterprise.apis.spectrumenterprise.net';
  }
};

/**
 * Generate a fake mock url to use for the path handlers that
 * the Mock Service will intercept.
 * Note: These are fake urls.
 * @param appShortName The app short name you want to build for.
 */
export const genMockHost = (appShortName: string): string =>
  `https://mock-${appShortName}.spectrumenterprise.net`;

/**
 * Generate mock endpoints for all API hosts
 * NOTE: Not a real url domain, just a placeholder that will be intercepted
 * by the Mock Service Worker.
 */
export const mockEndpoints = (): IAppConfigApiHosts => ({
  piHost: genMockHost('aim'),
  directHost: genMockHost('aim'),
  senetHost: genMockHost('senet'),
  directSSOHost: genMockHost('se-web'),
});

/**
 * Get the API Host name to use for API calls
 * @param appEnv current app env
 *
 * @note All lower environments will point to PINXT UAT by default with ENTDEL-17201. This accommodates the planned
 * deprecation PINXT QA env and provides more stable development and testing.
 *
 * You can point to PINXT DEV if needed by setting the VITE_APP_AUTH_HOST in GitLab when running a new pipeline.
 */
export const getApiHost = (appEnv: AppEnv): IAppConfigApiHosts => {
  const PINXT_HOST = {
    prod: 'https://apis.spectrum.net',
    stage: 'https://apis.stage-spectrum.net',
    uat: 'https://apis.shared.uat-spectrum.net',
    qa: 'https://apis.shared.qa-spectrum.net', // @deprecated
    dev: 'https://apis.shared.dev-spectrum.net',
  };

  switch (appEnv) {
    case AppEnv.Prod:
      return {
        piHost: PINXT_HOST.prod,
        directHost: '', // TODO: Figure out what this is
        senetHost: getProdSenetHost(),
        directSSOHost: getSSOProdHost(),
      };

    case AppEnv.Stage:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_STAGE || PINXT_HOST.stage,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_STAGE ||
          'https://fed-stage-p2.graphql.spectrumenterprise.net',
        senetHost:
          import.meta.env.VITE_APP_SENET_HOST_STAGE ||
          'https://stage.spectrumenterprise.net',
        directSSOHost:
          import.meta.env.VITE_APP_DIRECT_SSO_HOST_STAGE ||
          'https://stage-enterprise.apis.spectrumenterprise.net',
      };

    case AppEnv.EngProd:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_ENGPROD || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_ENGPROD ||
          'https://fed-engprod-p2.graphql.enterprise.spectrumtoolbox.com',
        senetHost:
          import.meta.env.VITE_APP_SENET_HOST_ENGPROD ||
          'https://develop-engprod.senet.spectrumtoolbox.com',
        directSSOHost:
          import.meta.env.VITE_APP_DIRECT_SSO_HOST_ENGPROD ||
          'https://engprod.se-web.spectrumtoolbox.com',
      };

    case AppEnv.Uat:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_UAT || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_UAT ||
          'https://fed-uat-p2.graphql.enterprise.spectrumtoolbox.com',
        senetHost:
          import.meta.env.VITE_APP_SENET_HOST_UAT ||
          'https://develop-uat.senet.spectrumtoolbox.com',
        directSSOHost:
          import.meta.env.VITE_APP_DIRECT_SSO_HOST_UAT ||
          'https://uat.se-web.spectrumtoolbox.com',
      };

    case AppEnv.QA:
      return {
        piHost: import.meta.env.VITE_APP_AUTH_HOST_QA || PINXT_HOST.uat,
        directHost:
          import.meta.env.VITE_APP_DIRECT_HOST_QA ||
          'https://fed-qa-p2.graphql.enterprise.spectrumtoolbox.com',
        senetHost:
          import.meta.env.VITE_APP_SENET_HOST_QA ||
          'https://develop-qa.senet.spectrumtoolbox.com',
        directSSOHost:
          import.meta.env.VITE_APP_DIRECT_SSO_HOST_QA ||
          'https://qa.se-web.spectrumtoolbox.com',
      };

    /** Currently no Dev Env, setup here if needed  */
    case AppEnv.Dev:
      return {
        piHost: '',
        directHost: import.meta.env.VITE_APP_DIRECT_HOST_DEV || '',
        senetHost: import.meta.env.VITE_APP_SENET_HOST_DEV || '',
        directSSOHost: import.meta.env.VITE_APP_DIRECT_SSO_HOST_DEV || '',
      };

    default:
      return mockEndpoints();
  }
};

/**
 * Get the full graphQL endpoint url
 * @param apiHost the current host
 * @param useDirectHost whether or not to use the direct host
 */
export const getGraphQlEndpoint = (
  apiHost: string,
  useDirectHost = false
): string => {
  if (useDirectHost) {
    return `${apiHost}/graphql`;
  }

  return `${apiHost}/entservices-graphql-p2/graphql`;
};

/**
 * Get the full REST endpoint url
 * @param apiHost the current host
 * @param useDirectHost whether or not to use the direct host
 */
export const getRestEndpoint = (
  apiHost: string,
  useDirectHost = false
): string => {
  if (useDirectHost) {
    return apiHost;
  }

  return `${apiHost}/entservices`;
};

/**
 * Get the correct device type name of the tdcs config to use.
 * @param appName current app name
 * @param appEnv current app env
 */
export const getTdcsDeviceType = (appName: string, appEnv: AppEnv): string => {
  switch (appEnv) {
    case AppEnv.QA:
      return `CLIENT-${appName}-QA`.toUpperCase();
    case AppEnv.Uat:
      return `CLIENT-${appName}-UAT`.toUpperCase();
    case AppEnv.EngProd:
      return `CLIENT-${appName}-ENGPROD`.toUpperCase();
    // Stage is prod-like and shares Prod's runtime config, same as Prod Inactive.
    case AppEnv.Stage:
    case AppEnv.Prod:
      return `CLIENT-${appName}-PROD`.toUpperCase();
    default:
      return `CLIENT-${appName}`.toUpperCase();
  }
};

/**
 * Whether or not the app environment is a prod based environment.
 * @param appEnv current app environment
 */
export const isProd = (appEnv: AppEnv): boolean => {
  return [AppEnv.Prod, AppEnv.Stage].some((env) => env === appEnv);
};

/**
 * Whether or not the app environment is the true, public facing, production environment.
 * @param appEnv current app environment
 */
export const isTrueProd = (): boolean => {
  return (
    window.location.hostname.toLowerCase() === 'cxdo.spectrumenterprise.net'
  );
};

/**
 * Whether or not the app environment is the Stage environment.
 * @param appEnv current app environment
 */
export const isStage = (appEnv: AppEnv): boolean => {
  return [AppEnv.Stage].some((env) => env === appEnv);
};

/**
 * Whether or not the app environment is Prod Inactive environment.
 * @param appEnv current app environment
 */
export const isProdInactive = (appEnv: AppEnv): boolean => {
  return isProd(appEnv) && !isTrueProd() && !isStage(appEnv);
};

/**
 * Whether or not the app environment is a mock based environment.
 * @param appEnv current app environment
 */
export const isMock = (appEnv: AppEnv): boolean => {
  return [AppEnv.Mock].some((env) => env === appEnv);
};

/**
 * Get the proper app env for Quantum Analytics
 *
 * Note: Stage maps to 'staging', not 'stage'. The QuantumAppEnvNameType union is
 * derived from the upstream Quantum event-case spec (linked above the type), and
 * 'staging' is the name that spec defines — 'stage' is not a valid value.
 * @param appEnv current app environment
 */
export const getQuantumEnvName = (appEnv: AppEnv): QuantumAppEnvNameType => {
  switch (appEnv) {
    case AppEnv.Dev:
      return 'dev';
    case AppEnv.QA:
      return 'qa';
    case AppEnv.Uat:
      return 'uat';
    case AppEnv.EngProd:
      return 'engprod';
    case AppEnv.Stage:
      return 'staging';
    case AppEnv.Prod:
      return 'prod';

    default:
      return 'mock';
  }
};

/**
 * Get the correct Env to use for DataDog RUM.
 */

export const getRumEnv = (appEnv: AppEnv): string => {
  if (isProdInactive(appEnv)) {
    return 'inactive';
  }
  return appEnv.toLowerCase();
};

/**
 * Get the correct Prod vs Lower Env Keys to use for DataDog RUM.
 */

export const getRumKeys = (): Pick<
  DataDog.RumInitConfiguration,
  'applicationId' | 'clientToken'
> => {
  return {
    applicationId: '7ee3f3a2-01c2-4765-aa36-ad86d823c1b4',
    clientToken: 'pubc40b665196471ebd2e49423f90334659',
  };
};

/**
 * Some parts of the application won't function correctly when ran locally.
 */
export const isLocalhost = (): boolean => {
  return window.location.hostname.toLowerCase().startsWith('localhost');
};
